import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Code,
  Search,
  Share2,
  Blocks,
  ShieldCheck,
  MessageCircle,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: Code,
    title: "Expert Website Development",
    description: "Custom websites built for speed, clarity, and conversion.",
    path: "/services/website-development",
    cta: "Learn More",
  },
  {
    icon: Search,
    title: "Professional SEO Services",
    description:
      "Search engine optimization services that help the right clients find you.",
    path: "/services/seo-services",
    cta: "Learn More",
  },
  {
    icon: Share2,
    title: "Social Media Services",
    description:
      "Consistent content and strategy that help your brand show up and build trust.",
    path: "/services/social-media-marketing",
    cta: "Learn More",
  },
  {
    icon: Blocks,
    title: "Custom Software Tools",
    description:
      "Dashboards, calculators, and custom web applications built around your workflow.",
    path: "/services/custom-tool-creation",
    cta: "Learn More",
  },
  {
    icon: ShieldCheck,
    title: "Website Maintenance Packages",
    description:
      "Ongoing updates, fixes, and technical support to keep everything performing well.",
    path: "/services/maintenance-support",
    cta: "Learn More",
  },
  {
    icon: MessageCircle,
    title: "Not Sure What You Need?",
    description:
      "Tell us the problem. We’ll help you find the right digital solution.",
    path: "/contact",
    cta: "Contact Us",
  },
];

const leftServices = [services[0], services[3], services[4]];
const rightServices = [services[1], services[2], services[5]];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
};

const OrbitDot = ({ className = "" }) => {
  return (
    <span
      className={`absolute z-20 h-2.5 w-2.5 rounded-full bg-primary shadow-[0_0_20px_rgba(124,58,237,0.55)] ${className}`}
    />
  );
};

const OrbitCenter = () => {
  return (
    <motion.div
      variants={itemVariants}
      className="relative mx-auto flex h-[240px] w-[240px] items-center justify-center sm:h-[270px] sm:w-[270px] lg:h-[285px] lg:w-[285px]"
    >
      <div className="absolute inset-8 rounded-full bg-primary/10 blur-3xl" />

      <div className="absolute inset-0 rounded-full border border-dashed border-primary/20" />
      <div className="absolute inset-5 rounded-full border border-primary/20" />
      <div className="absolute inset-10 rounded-full border border-primary/10" />
      <div className="absolute inset-[58px] rounded-full border border-primary/10" />

      <div className="absolute inset-[46px] rounded-full bg-primary/[0.06] blur-xl" />

      <OrbitDot className="left-1/2 top-[-5px] -translate-x-1/2" />
      <OrbitDot className="bottom-[-5px] left-1/2 -translate-x-1/2" />
      <OrbitDot className="left-[7%] top-[37%]" />
      <OrbitDot className="right-[7%] top-[37%]" />
      <OrbitDot className="bottom-[24%] left-[14%]" />
      <OrbitDot className="bottom-[24%] right-[14%]" />

      <div className="relative z-10 flex h-[170px] w-[170px] items-center justify-center rounded-full border border-primary/15 bg-background/95 px-5 text-center shadow-[0_18px_60px_rgba(124,58,237,0.16)] backdrop-blur-xl sm:h-[190px] sm:w-[190px] lg:h-[205px] lg:w-[205px]">
      <h3 className="font-display text-[19px] font-bold leading-[1.22] text-foreground sm:text-[21px] lg:text-[23px]">
  Complete Digital Solutions for Growing Businesses
</h3>
      </div>
    </motion.div>
  );
};

const ServiceItem = ({ service }) => {
  const Icon = service.icon;

  return (
    <motion.div variants={itemVariants}>
      <Link
        to={service.path}
        className="group relative flex gap-3 rounded-[22px] border border-transparent bg-background/45 p-3.5 transition-all duration-300 hover:-translate-y-1 hover:border-primary/15 hover:bg-background/90 hover:shadow-[0_14px_36px_rgba(15,23,42,0.08)] sm:p-4"
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-primary/15 bg-background shadow-[0_8px_26px_rgba(124,58,237,0.12)] transition-all duration-300 group-hover:scale-105 group-hover:border-primary/35 group-hover:bg-primary/10 sm:h-12 sm:w-12">
          <Icon className="h-5 w-5 text-primary sm:h-5 sm:w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="font-display text-[15px] font-semibold leading-snug text-foreground transition-colors duration-300 group-hover:text-primary sm:text-base">
  {service.title}
</h4>

          <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
            {service.description}
          </p>

          <div className="mt-2 inline-flex items-center gap-2 text-[13px] font-semibold text-primary transition-all duration-300 group-hover:gap-3 sm:text-sm">
            <span>{service.cta}</span>
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export const ServicesSection = () => {
  return (
    <section className="relative overflow-hidden bg-muted/30 py-8 sm:py-10 lg:py-10">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-90px] top-12 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 right-[-100px] h-[360px] w-[360px] rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute left-1/2 top-1/2 h-[460px] w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-background/55 blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="mx-auto mb-7 max-w-3xl text-center sm:mb-8 lg:mb-8"
        >
          <h2 className="font-display text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-[2.65rem]">
            What We Do
          </h2>

          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            We help businesses improve visibility, strengthen trust, and solve
            digital problems with practical services built around real goals.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="hidden items-center gap-5 lg:grid lg:grid-cols-[minmax(0,1fr)_285px_minmax(0,1fr)] xl:gap-7"
        >
          <div className="flex flex-col gap-3.5">
            {leftServices.map((service) => (
              <ServiceItem key={service.title} service={service} />
            ))}
          </div>

          <OrbitCenter />

          <div className="flex flex-col gap-3.5">
            {rightServices.map((service) => (
              <ServiceItem key={service.title} service={service} />
            ))}
          </div>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="lg:hidden"
        >
          <OrbitCenter />

          <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {services.map((service) => (
              <ServiceItem key={service.title} service={service} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
